import { Check, Clock, Star } from "lucide-react";
import { todayHoursText, weekHoursText } from "../../lib/opening-hours";
import type { BusinessFact } from "../../lib/business-facts";
import type { HeroFactsCardContent } from "./hero.business";
import React from "react";
import { cn } from "../../lib/utils";

/** One line of the card: a fact, or the opening hours. */
interface FactLine {
  kind: BusinessFact["kind"] | "hours";
  text: string;
  detail?: string;
}

/** The colour of the mark beside each fact: the theme's accent (bold's yellow) or its brand colour. */
type FactMarks = "accent" | "primary";

const
  /** The icon a fact line leads with. */
  FACT_ICONS: Record<FactLine["kind"], typeof Check> = {
    emergency: Check,
    hours: Clock,
    insured: Check,
    licensed: Check,
    rating: Star,
    years: Check,
  },

  /** Each mark colour, filled with its own readable foreground. */
  MARK_CLASSES: Record<FactMarks, string> = {
    accent: "jobsite:bg-accent jobsite:text-accent-foreground",
    primary: "jobsite:bg-primary jobsite:text-primary-foreground",
  },

  /**
   * A store subscription for a value that changes only between page loads.
   * @returns {() => void} An unsubscribe with nothing to undo.
   */
  subscribeToNothing = (): (() => void) => () => {
    // Nothing was subscribed, so there is nothing to undo.
  },

  /**
   * One line of the card: a coloured mark beside the fact and its smaller detail.
   * @param {object} props The line.
   * @param {FactLine} props.line What it says.
   * @param {FactMarks} props.marks The mark's colour.
   * @returns {React.JSX.Element} The list item.
   */
  FactLineItem = ({ line, marks }: { line: FactLine; marks: FactMarks }): React.JSX.Element => {
    const Icon = FACT_ICONS[line.kind];
    return (
      <li className="jobsite:flex jobsite:items-start jobsite:gap-3.5 jobsite:py-3.5 jobsite:first:pt-0 jobsite:last:pb-0">
        <span
          aria-hidden="true"
          className={cn("jobsite:flex jobsite:size-6 jobsite:shrink-0 jobsite:items-center jobsite:justify-center", MARK_CLASSES[marks])}
          style={{ borderRadius: "var(--jobsite-radius)" }}
        >
          <Icon className="jobsite:size-4" strokeWidth={3} />
        </span>
        <span className="jobsite:min-w-0 jobsite:pt-px">
          <span className="jobsite:block jobsite:text-base jobsite:font-bold jobsite:leading-snug jobsite:sm:text-[1.0625rem]">{line.text}</span>
          {(line.detail ?? "") !== "" && (
            <span className="jobsite:mt-0.5 jobsite:block jobsite:text-sm jobsite:text-muted-foreground">{line.detail}</span>
          )}
        </span>
      </li>
    );
  },

  /**
   * The split hero's facts card: what is true about the business, one line
   * each. The headline and the header already name it, so the card does not.
   * Today's hours need today's date, so the server renders the week and the
   * browser swaps in "Open today, 7am to 5pm" once it knows the day.
   * @param {object} props The card.
   * @param {string} props.className The card's surface classes.
   * @param {HeroFactsCardContent} props.content What the card says.
   * @param {FactMarks} [props.marks] The colour beside each fact; the accent unless the look says otherwise.
   * @returns {React.JSX.Element | null} The card, or null when there is nothing true to say.
   */
  HeroFactsCard = ({
    className,
    content,
    marks = "accent",
  }: { className: string; content: HeroFactsCardContent; marks?: FactMarks }): React.JSX.Element | null => {
    const weekLine = weekHoursText(content.hours),
      // Today is the visitor's clock, which the server does not have: the server
      // (and hydration) render no today line, then the browser renders its own.
      todayLine = React.useSyncExternalStore(
        subscribeToNothing,
        () => todayHoursText(content.hours, new Date(), content.timezone),
        () => null,
      ),
      offer = content.offer?.trim() ?? "",
      lines: FactLine[] = [
        ...content.facts,
        ...(weekLine === "" ? [] : [{ detail: weekLine, kind: "hours" as const, text: todayLine ?? "Hours" }]),
      ];

    if (lines.length === 0 && offer === "") {
      return null;
    }

    return (
      <div className={cn(className, "jobsite:p-6 jobsite:sm:p-8")} style={{ borderRadius: "var(--jobsite-radius)" }}>
        {lines.length > 0 && (
          <ul className="jobsite:divide-y jobsite:divide-border">
            {lines.map((line) => <FactLineItem key={line.kind} line={line} marks={marks} />)}
          </ul>
        )}
        {offer !== "" && (
          <p
            className={cn(
              "jobsite:font-heading jobsite:text-lg jobsite:uppercase jobsite:leading-tight",
              lines.length > 0 && "jobsite:mt-5 jobsite:border-t-2 jobsite:border-foreground jobsite:pt-4",
            )}
          >
            {offer}
          </p>
        )}
      </div>
    );
  };

export { HeroFactsCard };
